import { Localization } from "@viamap/viamap2-common";
import { fetchTingbogDocumentBlob, getTingbogDownloadTarget } from "src/managers/TingbogDownload";
import { cutPdfToSelectedPages, getPdfPageCount } from "src/modules/ANNModule/ANNPDFCutter";
import { createANNLLMAnalyzer, getANNLLMAnalyzer, type LLMAnalysisResultItem, type LLMMetadata } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { downloadOverlayImage } from "src/modules/ANNModule/ANNImageOverlayStorage";
import {
   clampPagesToDocument,
   getAnnotationSourceExtractedPages,
   isFullCutAnnotationSelection,
   mapAktPagesToCutSelection,
   normalizePages,
} from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import { withSkippedPaataleResolution } from "./llm";
import { createAbortError } from "./jobs";
import { electronicPackageForRow, isDigitalRow, isUserFileDocument, type ServitutRow } from "../model/row";
import { selectDocumentSourceKind, selectRowAktId, selectWorkingDocumentLabel } from "../store/selectors";

export function servitutTekster(servitut: any): string[] {
   const tekster: string[] = [];
   if (Array.isArray(servitut?.tekster)) {
      tekster.push(...servitut.tekster.map((t: any) => t?.tekst ?? t).filter(Boolean));
   }
   if (servitut?.tekstSummarisk) {
      tekster.push(servitut.tekstSummarisk);
   }
   return tekster;
}

export function documentS3Key(row: ServitutRow): string | undefined {
   if (row.document.kind === "upload" || row.document.kind === "annotation") {
      return row.document.s3Key;
   }
   return undefined;
}

export function documentFilename(row: ServitutRow): string {
   return selectWorkingDocumentLabel(row);
}

export function usesLocalPdfForLocate(row: ServitutRow): boolean {
   return row.document.kind === "upload" || row.document.kind === "annotation";
}

export function buildRowMetadata(
   row: ServitutRow,
   existing?: LLMMetadata,
   caseDescription?: string,
): LLMMetadata {
   const normalizedCase = caseDescription?.trim() || existing?.caseDescription || null;
   if (row.document.kind === "upload" || isUserFileDocument(row)) {
      return {
         id: row.id,
         document: row.document.kind === "upload" || row.document.kind === "annotation"
            ? (row.document.filename || "servitut.pdf")
            : "servitut.pdf",
         documentUrl: null,
         aktId: row.id,
         tekster: [...servitutTekster(row.landRegistry.servitut), row.landRegistry.summary].filter(Boolean),
         fromOvrigDataSource: false,
         caseDescription: normalizedCase,
      };
   }
   if (isDigitalRow(row)) {
      if (existing && existing.aktId == null) {
         return { ...existing, id: row.id, caseDescription: normalizedCase };
      }
      return {
         id: row.id,
         document: existing?.document || documentFilename(row),
         documentUrl: null,
         aktId: null,
         tekster: [...servitutTekster(row.landRegistry.servitut), row.landRegistry.summary].filter(Boolean),
         fromOvrigDataSource: null,
         caseDescription: normalizedCase,
      };
   }
   if (existing) {
      return { ...existing, id: row.id, caseDescription: normalizedCase };
   }
   return {
      id: row.id,
      document: documentFilename(row),
      documentUrl: row.document.kind === "tingbog_akt" ? row.document.url || null : null,
      aktId: selectRowAktId(row) || row.identity.aktId || row.id,
      tekster: [...servitutTekster(row.landRegistry.servitut), row.landRegistry.summary].filter(Boolean),
      fromOvrigDataSource: row.document.kind === "tingbog_akt" && row.document.source === "ovrigAkt",
      caseDescription: normalizedCase,
   };
}

async function fetchElectronicPackage(row: ServitutRow): Promise<Blob | null> {
   const electronic = electronicPackageForRow(row);
   const target = electronic ? getTingbogDownloadTarget(electronic) : undefined;
   if (!target) {
      return null;
   }
   const blob = await fetchTingbogDocumentBlob(target);
   return blob && blob.size > 0 ? blob : null;
}

export async function getRowSourcePdfBlob(row: ServitutRow, signal?: AbortSignal): Promise<Blob | null> {
   const key = documentS3Key(row);
   if (electronicPackageForRow(row) && !isUserFileDocument(row)) {
      try {
         const digital = await fetchElectronicPackage(row);
         if (digital) {
            return digital;
         }
      } catch (error) {
         console.warn("[Servitutredegørelse] digital package open failed, trying saved file", row.id, error);
      }
   }
   if (key) {
      try {
         const { blob } = await downloadOverlayImage(key);
         if (blob && blob.size > 0) {
            return blob;
         }
      } catch (error) {
         console.warn("[Servitutredegørelse] saved file open failed", row.id, error);
      }
   }
   if (electronicPackageForRow(row)) {
      return fetchElectronicPackage(row);
   }
   const aktId = selectRowAktId(row) || (row.registryBinding?.kind === "tingbog_akt" ? row.registryBinding.aktId : undefined);
   if (!aktId) {
      return null;
   }
   return getANNLLMAnalyzer().fetchPdf(aktId, signal);
}

export async function getCutPdfForContent(row: ServitutRow, signal?: AbortSignal): Promise<Blob | null> {
   const aktPages = normalizePages(row.pages.selected.length ? row.pages.selected : row.pages.aiSuggested);
   if (isDigitalRow(row) && !isUserFileDocument(row)) {
      return null;
   }
   if (row.document.kind === "annotation") {
      const { blob } = await downloadOverlayImage(row.document.s3Key);
      const extracted = row.document.extractedAktPages || [];
      if (extracted.length) {
         const localPages = mapAktPagesToCutSelection(aktPages, extracted);
         if (localPages?.length && localPages.length !== extracted.length) {
            return cutPdfToSelectedPages(blob, localPages);
         }
         return blob;
      }
      return aktPages.length ? cutPdfToSelectedPages(blob, aktPages) : blob;
   }
   if (row.document.kind === "upload") {
      const { blob } = await downloadOverlayImage(row.document.s3Key);
      if (!aktPages.length) {
         return blob;
      }
      const pageCount = await getPdfPageCount(blob).catch(() => 0);
      const pages = pageCount ? clampPagesToDocument(aktPages, pageCount) : aktPages;
      return pages.length ? cutPdfToSelectedPages(blob, pages) : blob;
   }
   const source = await getRowSourcePdfBlob(row, signal);
   if (!source) {
      return null;
   }
   return aktPages.length ? cutPdfToSelectedPages(source, aktPages) : source;
}

export async function locateUploadedRow(
   row: ServitutRow,
   metadata: LLMMetadata,
   analyzer: ReturnType<typeof createANNLLMAnalyzer>,
   signal: AbortSignal,
): Promise<LLMAnalysisResultItem> {
   const key = documentS3Key(row);
   if (!key) {
      throw new Error(Localization.getText("No PDF available for this document"));
   }
   const localPdf = await downloadOverlayImage(key);
   if (signal.aborted) {
      throw createAbortError();
   }
   const result = await analyzer.locateServitutDocument({
      metadata: withSkippedPaataleResolution(metadata),
      pdfBlob: localPdf.blob,
      documentFileName: documentFilename(row) || localPdf.fileName || "servitut.pdf",
      signal,
   });
   if (signal.aborted) {
      throw createAbortError();
   }
   return { ...result, id: row.id };
}

export function rowPdfCacheKey(row: ServitutRow, kind: "original" | "source"): string | null {
   const key = documentS3Key(row);
   if (key) {
      return `${kind}:${selectDocumentSourceKind(row)}:${key}`;
   }
   const aktId = selectRowAktId(row) || (row.registryBinding?.kind === "tingbog_akt" ? row.registryBinding.aktId : undefined);
   if (aktId) {
      return `${kind}:akt:${aktId}`;
   }
   return null;
}

export async function getCachedRowPdfBlob(
   row: ServitutRow,
   cache?: Map<string, Promise<Blob | null>>,
   signal?: AbortSignal,
): Promise<Blob | null> {
   if (!cache) {
      return getRowSourcePdfBlob(row, signal);
   }
   const key = rowPdfCacheKey(row, "source");
   if (!key) {
      return getRowSourcePdfBlob(row, signal);
   }
   const existing = cache.get(key);
   if (existing) {
      return existing;
   }
   const pending = getRowSourcePdfBlob(row, signal).catch((error) => {
      if (cache.get(key) === pending) {
         cache.delete(key);
      }
      throw error;
   });
   cache.set(key, pending);
   return pending;
}

export async function cutExportPdf(row: ServitutRow, pdfBlob: Blob): Promise<Blob> {
   const pagesToKeep = normalizePages(row.pages.selected);
   if (row.document.kind === "annotation") {
      const pageCount = await getPdfPageCount(pdfBlob).catch(() => 0);
      const extracted = row.document.extractedAktPages || getAnnotationSourceExtractedPages({
         annotationDocument: { extractedPages: row.document.extractedAktPages },
      });
      let localPages = pagesToKeep.length && extracted.length
         ? mapAktPagesToCutSelection(pagesToKeep, extracted)
         : null;
      if (
         !localPages?.length
         && pagesToKeep.length
         && pageCount
         && pagesToKeep.every((page) => page >= 1 && page <= pageCount)
      ) {
         localPages = pagesToKeep;
      }
      if (!localPages?.length || (pageCount > 0 && isFullCutAnnotationSelection(localPages, pageCount))) {
         return pdfBlob;
      }
      try {
         return await cutPdfToSelectedPages(pdfBlob, localPages);
      } catch (error) {
         console.warn("[Servitutredegørelse] export cut failed, using saved annotation PDF", row.id, error);
         return pdfBlob;
      }
   }
   if (isDigitalRow(row) && !isUserFileDocument(row)) {
      return pdfBlob;
   }
   if (!pagesToKeep.length) {
      throw new Error("Ingen sider valgt.");
   }
   return cutPdfToSelectedPages(pdfBlob, pagesToKeep);
}
